import {Injectable} from '@angular/core';
import {AuthorizationService} from '../authorization/authorization.service';
import {concatMap, expand, filter, map, pairwise, startWith, take, tap} from 'rxjs/operators';
import {BehaviorSubject, combineLatest, Observable, Subscription, timer} from 'rxjs';
import {UserModel} from '../user/user-list/user.model';
import {environment} from '../../environments/environment';
import {HttpClient, HttpParams} from '@angular/common/http';
import {ImmutableListRepositoryService} from '../../lib/gelios-field/immutable-list-repository.service';
import * as moment from 'moment';
import {BillingVer202212StatusForLayout} from './billing-ver202212-status-for-layout';
import {BillingVer202212Status} from '../user/user-list/billing-ver-2022-12-status';
import {ModalWindowService} from '../shared/modal-window/modal-window.service';
import {ModalWindowParameters} from '../shared/modal-window/modal-window-parameters';
import {ModalWindowType} from '../shared/modal-window/modal-window-type';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class UserBalanceService {

  pollObservable: Observable<any>;
  pollSubscription: Subscription;
  public user: BehaviorSubject<UserModel> = new BehaviorSubject<UserModel>(null);
  refreshInterval = 10000;

  public simBalance: Observable<{show: boolean, value: string, isAlarming: boolean}>;
  public simFlashCallBalance: Observable<{show: boolean, value: string, isAlarming: boolean}>;
  public userBillingVer202212Status: Observable<BillingVer202212StatusForLayout>;
  public insuranceBalance: Observable<{show: boolean, value: string, isAlarming: boolean}>;
  public overallIsAlarming: Observable<boolean>;
  public overallShow: Observable<boolean>;
  public paymentIsPossible: Observable<boolean>;
  showBalanceForRole(user: UserModel) {
    return AuthorizationService.userRoleIsExactlyAdmin(user)
      || AuthorizationService.userRoleIsExactlyIntegrator(user);
  }

  constructor(
    protected authorizationService: AuthorizationService,
    protected immutableList: ImmutableListRepositoryService,
    protected http: HttpClient,
    protected modalWindowService: ModalWindowService,
    protected translateService: TranslateService,
    protected router: Router,
  ) {
    combineLatest(
      this.authorizationService.getIdentityObservable(),
      this.immutableList.ready()
    ).pipe(
      filter(
        ([user, ready]) => !!user && ready
      ),
      take(1)
    ).subscribe(
      ([userFromAuth, ready]) => {
        if (this.pollSubscription) {
          this.pollSubscription.unsubscribe();
        }
        const httpParams = new HttpParams()
          .append(
            'fields',
            [
              'id',
              'role',
              'balance',
              'sim_manager_id',
              'sims_balance',
              'sims_balance_flash_call',
              'user_billing_acquirer_id',
            ].join(',')
          ).append(
            'expand',
            [
              'billingVer202212Status',
              'userBillingAcquirerCurrencyCode',
            ].join(',')
          );
        this.pollObservable = this.http.get<UserModel>(
          environment.apiBasepoint + 'user/' + userFromAuth.id,
          {params: httpParams}
        ).pipe(
          tap(
            user => this.user.next(user)
          )
        );
        this.pollSubscription = this.pollObservable
          .pipe(expand(() => timer(this.refreshInterval).pipe(concatMap(() => this.pollObservable))))
          .subscribe();
      }
    );

    this.simBalance = this.user.pipe(
      filter(
        user => !!user
      ),
      map(
        user => ({
          show: this.showBalanceForRole(user) && !!user.sim_manager_id,
          value: (Math.round(user.sims_balance * 100) / 100).toString(),
          isAlarming: user.sims_balance < 10
        })
      )
    );
    this.simFlashCallBalance = this.user.pipe(
      filter(
        user => !!user
      ),
      map(
        user => ({
          show: false,
          value: (Math.round(user.sims_balance_flash_call * 100) / 100).toString(),
          isAlarming: user.sims_balance_flash_call <= 0
        })
      )
    );
    this.userBillingVer202212Status = this.user.pipe(
      filter(
        user => !!user
      ),
      map(
        user => {
          const show = this.showBalanceForRole(user) && !!user.billingVer202212Status
            && !!user.billingVer202212Status.currencyAbbreviation;
          if (show) {
            const result: BillingVer202212StatusForLayout = {
              show: true,
              isAlarming: this.balanceV202212IsAlarming(user.billingVer202212Status),
              isOk: user.billingVer202212Status.balance > 0,
              paymentTypeLabel: this.immutableList.getItemById(
                'user-billing-payment-type',
                user.billingVer202212Status.paymentType
              ),
              paymentDeadlineMissed: user.billingVer202212Status.daysWithNegativeBalance >
                user.billingVer202212Status.maxDaysWithNegativeBalance,
              balanceLabel: user.billingVer202212Status.currencyAbbreviation ?
                user.billingVer202212Status.balance + ' ' + user.billingVer202212Status.currencyAbbreviation
                : user.billingVer202212Status.balance.toString(),
              paymentDeadlineLabel: moment(user.billingVer202212Status.paymentDeadline, 'YYYY-MM-DD').format('DD.MM.YYYY'),
              billingVer202212Status: user.billingVer202212Status
            };
            return result;
          }
          return {
            show: false
          };
        }
      )
    );

    this.insuranceBalance = this.user.pipe(
      filter(
        user => !!user
      ),
      map(
        user => ({
          // tslint:disable-next-line:max-line-length
          show: false,
          value: '', // (Math.round(identity.insurance_balance * 100) / 100).toString(),
          isAlarming: false, // identity.insurance_balance < 0,
        })
      )
    );



    // this.overallIsAlarming = combineLatest(
    //   this.simBalance, this.userBalance, this.simFlashCallBalance, this.insuranceBalance
    // ).pipe(
    //   map(
    //     ([simBalance, userBalance, simFlashCallBalance, insuranceBalance]) =>
    //       (simBalance.show && simBalance.isAlarming) || (userBalance.show && userBalance.isAlarming)
    //       || (simFlashCallBalance.show && simFlashCallBalance.isAlarming)    || (insuranceBalance.show && insuranceBalance.isAlarming)
    //   )
    // );
    this.overallIsAlarming = combineLatest(
      this.simBalance, this.userBillingVer202212Status
    ).pipe(
      map(
        ([simBalance, userBillingVer202212Status]) =>
          (simBalance.show && simBalance.isAlarming)
          || (userBillingVer202212Status.show && userBillingVer202212Status.isAlarming)
      )
    );
    // this.overallShow = combineLatest(
    //   this.simBalance, this.userBalance, this.simFlashCallBalance, this.insuranceBalance
    // ).pipe(
    //   map(
    //     ([simBalance, userBalance, simFlashCallBalance, insuranceBalance]) =>
    //       simBalance.show || userBalance.show || simFlashCallBalance.show  || insuranceBalance.show
    //   )
    // );
    this.overallShow = combineLatest(
      this.simBalance, this.userBillingVer202212Status
    ).pipe(
      map(
        ([simBalance, userBillingVer202212Status]) =>
          simBalance.show || userBillingVer202212Status.show
      )
    );
    this.userBillingVer202212Status.pipe(
      startWith(null),
      pairwise()
    ).subscribe(
      ([status1, status2]) => {
        const alarming1 = status1 && status1.billingVer202212Status ?
          status1.billingVer202212Status.unitsBlocked > 0 : false;
        const alarming2 = status2 && status2.billingVer202212Status ?
          status2.billingVer202212Status.unitsBlocked > 0 : false;
        if (!alarming1 && alarming2) {
          const modalWindowParams: ModalWindowParameters = {
            modalWindowType: ModalWindowType.LowBalance,
            text: this.translateService.instant('Insufficient funds to continue service') + '.<br>'
              + status2.billingVer202212Status.unitsBlocked.toString(10) + ' '
              + this.translateService.instant('out of') + ' '
              + status2.billingVer202212Status.totalUnits.toString(10) + ' '
              + this.translateService.instant('of your units were hidden and not available for rent to clients') + '.<br>'
              + this.translateService.instant('To resume service, you need to top up the balance') + '.<br>'
              + this.translateService.instant('If you have any questions, please contact your manager') + '.<br>'
            ,
          };
          this.modalWindowService.showWindow(modalWindowParams);
        }
      }
    );
    this.paymentIsPossible = this.user.pipe(
      map(
        user => !!user.user_billing_acquirer_id
      )
    );
  }


  balanceV202212IsAlarming(billingStatus: BillingVer202212Status) {
    return billingStatus.balance < 0;
  }
}
